import React, { useState, useCallback } from 'react';
import './VirtualKeyboard.css';

interface VirtualKeyboardProps {
  onKeyPress: (key: string) => void;
  onClose: () => void;
  visible: boolean;
  inputType?: 'text' | 'licensePlate' | 'numeric';
}

const KEYBOARD_LAYOUTS = {
  default: [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'ß', '´', '{backspace}'],
    ['q', 'w', 'e', 'r', 't', 'z', 'u', 'i', 'o', 'p', 'ü', '+'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ö', 'ä', '#'],
    ['{shift}', 'y', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '-', '{shift}'],
    ['', '{space}', '', '{close}', ''],
  ],
  shift: [
    ['!', '"', '§', '$', '%', '&', '/', '(', ')', '=', '?', '`', '{backspace}'],
    ['Q', 'W', 'E', 'R', 'T', 'Z', 'U', 'I', 'O', 'P', 'Ü', '*'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ö', 'Ä', "'"],
    ['{shift}', 'Y', 'X', 'C', 'V', 'B', 'N', 'M', ';', ':', '_', '{shift}'],
    ['', '{space}', '', '{close}', ''],
  ],
  licensePlate: [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '{backspace}'],
    ['Q', 'W', 'E', 'R', 'T', 'Z', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['Y', 'X', 'C', 'V', 'B', 'N', 'M'],
    ['', '{space}', '', '{close}', ''],
  ],
  numeric: [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['+', '0', '{backspace}'],
    ['{close}'],
  ],
};

export default function VirtualKeyboard({ onKeyPress, onClose, visible, inputType = 'text' }: VirtualKeyboardProps) {
  const [shiftActive, setShiftActive] = useState(false);

  const handleKeyPress = useCallback(
    (key: string) => {
      if (key === '{backspace}') {
        onKeyPress('Backspace');
      } else if (key === '{shift}') {
        setShiftActive(!shiftActive);
      } else if (key === '{space}') {
        onKeyPress(' ');
      } else if (key === '{close}') {
        onClose();
      } else {
        onKeyPress(key);
        // Auto-deactivate shift after character input (for single character uppercase)
        if (shiftActive) {
          setShiftActive(false);
        }
      }
    },
    [onKeyPress, onClose, shiftActive]
  );

  if (!visible) return null;

  // Choose layout based on inputType
  let currentLayout;
  if (inputType === 'numeric') {
    currentLayout = KEYBOARD_LAYOUTS.numeric;
  } else if (inputType === 'licensePlate') {
    currentLayout = KEYBOARD_LAYOUTS.licensePlate;
  } else {
    currentLayout = shiftActive ? KEYBOARD_LAYOUTS.shift : KEYBOARD_LAYOUTS.default;
  }

  return (
    <div className="virtual-keyboard-overlay" onClick={onClose}>
      <div className="virtual-keyboard-container" onClick={(e) => e.stopPropagation()}>
        <div className="virtual-keyboard">
          {currentLayout.map((row, rowIndex) => (
            <div key={rowIndex} className="keyboard-row">
              {row.map((key, keyIndex) => {
                if (key === '') return <span key={keyIndex} className="keyboard-key placeholder" style={{visibility: 'hidden'}} />;
                let className = 'keyboard-key';
                let displayKey = key;

                if (key === '{backspace}') {
                  className += ' key-backspace';
                  displayKey = '⌫';
                } else if (key === '{shift}') {
                  className += ' key-shift';
                  if (shiftActive) className += ' active';
                  displayKey = '⇧';
                } else if (key === '{space}') {
                  className += ' key-space';
                  displayKey = 'Leerzeichen';
                } else if (key === '{close}') {
                  className += ' key-close';
                  displayKey = '✕ Schließen';
                }

                return (
                  <button
                    key={keyIndex}
                    className={className}
                    onClick={() => handleKeyPress(key)}
                  >
                    {displayKey}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
